import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useUserContext } from "@/context/AuthContext";

export default function AuthLayout() {
  const { isAuthenticated, isLoading } = useUserContext();
  const location = useLocation();

  // Check if we're on user guide page
  const isUserGuidePage = location.pathname === "/busiconn-user-guide";

  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get("redirect") || "/";

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex-center w-full h-full">
        <div className="flex-center flex-col gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <p className="text-light-3">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated ? (
        <Navigate to={redirectTo} replace />
      ) : (
        <div className="w-full lg:grid lg:min-h-[600px] lg:grid-cols-2 xl:min-h-[800px]">
          {/* Left side - Form */}
          <div className={`flex items-center justify-center py-12 ${isUserGuidePage ? "lg:col-span-2" : ""}`}>
            <div className="mx-auto grid w-[350px] gap-6">
              <Outlet />
            </div>
          </div>

          {/* Right side - Branding/Info - HIDE on user guide page */}
          {!isUserGuidePage && (
            <div className="hidden bg-muted lg:block relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.22),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.14),transparent_38%)]" />
              <div className="absolute top-16 right-16 h-24 w-24 rounded-full bg-primary-500/20 blur-3xl auth-orb" />
              <div className="absolute bottom-24 left-12 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl auth-orb auth-orb-delayed" />

              <div className="relative flex h-full flex-col justify-center px-10">
                <div className="max-w-xl">
                  <div className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 bg-primary-500/10 px-3 py-1 text-xs font-medium text-primary-300">
                    <span className="h-2 w-2 rounded-full bg-primary-400 auth-dot" />
                    Connect. Chat. Trade with confidence.
                  </div>

                  <h1 className="mt-5 text-4xl font-bold text-light-1 leading-tight">
                    Start with confidence.
                  </h1>
                  <p className="mt-3 text-light-3 max-w-lg">
                    Connect with buyers, sellers, freelancers, and resellers. Agree on the details and use protected payments to move your business forward.
                  </p>

                  <div className="mt-8 grid gap-3">
                    <div className="rounded-2xl border border-dark-4 bg-dark-2/80 p-4 auth-float shadow-xl shadow-black/10">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-semibold text-light-1">Discover</div>
                          <div className="text-xs text-light-3">Products, services, and business opportunities</div>
                        </div>
                        <div className="h-10 w-10 rounded-full bg-primary-500/15 grid place-items-center text-primary-300">
                          01
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-dark-4 bg-dark-2/80 p-4 auth-float auth-float-delayed shadow-xl shadow-black/10">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-semibold text-light-1">Chat & negotiate</div>
                          <div className="text-xs text-light-3">Move the deal forward without friction</div>
                        </div>
                        <div className="h-10 w-10 rounded-full bg-primary-500/15 grid place-items-center text-primary-300">
                          02
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-dark-4 bg-dark-2/80 p-4 auth-float shadow-xl shadow-black/10">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-semibold text-light-1">Protect & close</div>
                          <div className="text-xs text-light-3">Protected payment, delivery review, and payout tracking</div>
                        </div>
                        <div className="h-10 w-10 rounded-full bg-primary-500/15 grid place-items-center text-primary-300">
                          03
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-2 text-xs text-light-3">
                    <span className="rounded-full border border-dark-4 bg-dark-2 px-3 py-1">Physical goods</span>
                    <span className="rounded-full border border-dark-4 bg-dark-2 px-3 py-1">Digital goods</span>
                    <span className="rounded-full border border-dark-4 bg-dark-2 px-3 py-1">Services</span>
                    <span className="rounded-full border border-dark-4 bg-dark-2 px-3 py-1">Protected payments</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
